import api from './api';

export interface LoginRequest {
  username: string;
  password: string;
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  invite_code?: string;
  telegram?: string;
  wechat?: string;
}

export interface LoginResponse {
  user: {
    id: number;
    username: string;
    email: string;
    user_type: 'admin' | 'client' | 'sub';
    status: 'active' | 'disabled';
    client_id: number;
    parent_id?: number;
    created_at: string;
    updated_at: string;
  };
  token: string;
  permissions?: string[];
  roles?: string[];
}

export interface ProfileResponse {
  user: {
    id: number;
    username: string;
    email: string;
    user_type: 'admin' | 'client' | 'sub';
    status: 'active' | 'disabled';
    client_id: number;
    parent_id?: number;
    api_key?: string;
    created_at: string;
    updated_at: string;
  };
}

export interface UpdateProfileRequest {
  username?: string;
  email?: string;
}

export interface ChangePasswordRequest {
  old_password: string;
  new_password: string;
}

class AuthService {
  // 用户登录
  async login(data: LoginRequest): Promise<LoginResponse> {
    const response = await api.post('/auth/login', data);
    const responseData = response.data;

    // 检查响应格式
    if (responseData.success && responseData.data) {
      return {
        user: {
          id: responseData.data.user.id,
          username: responseData.data.user.username,
          email: responseData.data.user.email,
          user_type: responseData.data.user.user_type || 'admin',
          status: responseData.data.user.status || 'active',
          client_id: responseData.data.user.client_id || 1,
          created_at: responseData.data.user.created_at || new Date().toISOString(),
          updated_at: responseData.data.user.updated_at || new Date().toISOString(),
        },
        token: responseData.data.token,
        permissions: responseData.data.permissions || [],
        roles: responseData.data.roles || [],
      };
    } else {
      throw new Error(responseData.message || '登录失败');
    }
  }

  // 用户注册
  async register(data: RegisterRequest): Promise<LoginResponse> {
    const response = await api.post('/auth/register', data);
    const responseData = response.data;

    if (responseData.success && responseData.data) {
      return {
        user: {
          id: responseData.data.user.id,
          username: responseData.data.user.username,
          email: responseData.data.user.email,
          user_type: responseData.data.user.user_type || 'client',
          status: responseData.data.user.status || 'active',
          client_id: responseData.data.user.client_id || 0,
          created_at: responseData.data.user.created_at || new Date().toISOString(),
          updated_at: responseData.data.user.updated_at || new Date().toISOString(),
        },
        token: responseData.data.token,
        permissions: responseData.data.permissions || [],
        roles: responseData.data.roles || [],
      };
    } else {
      throw new Error(responseData.message || '注册失败');
    }
  }

  // 用户退出
  async logout(): Promise<void> {
    await api.post('/auth/logout');
  }

  // 获取用户信息
  async getProfile(): Promise<ProfileResponse> {
    const response = await api.post('/api/users/profile');
    // 后端返回的数据结构是 { code, data, message, success }
    // 需要将 data 包装成 { user: data } 的格式
    return {
      user: response.data.data
    };
  }

  // 更新用户信息
  async updateProfile(data: UpdateProfileRequest): Promise<ProfileResponse> {
    const response = await api.post('/api/users/profile/update', data);
    return response.data;
  }

  // 修改密码
  async changePassword(data: ChangePasswordRequest): Promise<void> {
    await api.post('/api/users/change-password', data);
  }

  // 生成API密钥
  async generateAPIKey(): Promise<{ api_key: string }> {
    const response = await api.post('/api/users/api-key/generate');
    return response.data;
  }

  // 获取API密钥
  async getAPIKey(): Promise<{ api_key: string }> {
    const response = await api.post('/api/users/api-key/get');
    return response.data;
  }

  // 重置API密钥
  async resetAPIKey(): Promise<{ api_key: string }> {
    const response = await api.post('/api/users/api-key/reset');
    return response.data;
  }
}

export const authService = new AuthService();